import {
	Button,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import { Building2, ChevronRight, Loader2, Search } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge } from "src/components/status-badge";
import userProvider, {
	type MerchantDetails,
	type PaginatedMerchantsResponse,
} from "src/providers/user-provider";

const ManageMerchantsPage: React.FC = () => {
	const [merchants, setMerchants] = React.useState<MerchantDetails[]>([]);
	const [totalCount, setTotalCount] = React.useState(0);
	const [isLoading, setIsLoading] = React.useState(true);
	const [currentPage, setCurrentPage] = React.useState(1);
	const [searchQuery, setSearchQuery] = React.useState("");
	const [debouncedSearch, setDebouncedSearch] = React.useState("");
	const navigate = useNavigate();

	const itemsPerPage = 20;

	// Debounce search
	React.useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchQuery);
			setCurrentPage(1); // Reset to first page on new search
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	const loadMerchants = React.useCallback(
		async (page: number, search: string) => {
			try {
				setIsLoading(true);
				const offset = (page - 1) * itemsPerPage;

				const response: PaginatedMerchantsResponse =
					await userProvider.listMerchants({
						limit: itemsPerPage,
						offset,
						search: search,
						includeDeleted: false,
					});

				setMerchants(response.merchants);
				setTotalCount(response.totalCount);
			} catch (error) {
				console.error("Failed to load merchants:", error);
				toast.error("Failed to load merchants", {
					description: "Please try again later",
				});
			} finally {
				setIsLoading(false);
			}
		},
		[],
	);

	React.useEffect(() => {
		loadMerchants(currentPage, debouncedSearch);
	}, [currentPage, debouncedSearch, loadMerchants]);

	const totalPages = Math.ceil(totalCount / itemsPerPage);

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<div className="p-4 sm:p-6">
			<div className="flex items-center justify-between mb-6">
				<div>
					<h1 className="text-3xl font-bold">Merchant Management</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total merchant{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			<div className="mb-6">
				<div className="relative max-w-md">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Search by merchant name or creator email..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10"
					/>
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center items-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : merchants.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 px-8 text-center border rounded-lg bg-muted/50">
					<div className="space-y-4 max-w-md">
						<div className="space-y-2">
							<Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
							<h3 className="text-lg font-semibold">
								{searchQuery ? "No merchants found" : "No merchants yet"}
							</h3>
							<p className="text-sm text-muted-foreground">
								{searchQuery
									? "Try adjusting your search query"
									: "Merchants will appear here once users create them"}
							</p>
						</div>
					</div>
				</div>
			) : (
				<>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Merchant Name</TableHead>
								<TableHead>Website</TableHead>
								<TableHead>Creator</TableHead>
								<TableHead>Created At</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="w-8" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{merchants.map((merchant) => (
								<TableRow
									key={merchant.uuid}
									onClick={() => navigate(`/merchants/${merchant.uuid}`)}
									className="cursor-pointer"
								>
									<TableCell className="font-medium">{merchant.name}</TableCell>
									<TableCell>
										{merchant.website ? (
											<a
												href={merchant.website}
												target="_blank"
												rel="noopener noreferrer"
												className="text-blue-600 hover:underline"
											>
												{merchant.website}
											</a>
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell>
										<div className="flex flex-col">
											<span className="font-medium">
												{merchant.creatorName || "Unknown"}
											</span>
											<span className="text-sm text-muted-foreground">
												{merchant.creatorEmail || "—"}
											</span>
										</div>
									</TableCell>
									<TableCell>{formatDate(merchant.createdAt)}</TableCell>
									<TableCell>
										{merchant.deletedAt ? (
											<StatusBadge type="error">Deleted</StatusBadge>
										) : (
											<StatusBadge type="success">Active</StatusBadge>
										)}
									</TableCell>
									<TableCell>
										<ChevronRight className="h-4 w-4 text-muted-foreground" />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{totalPages > 1 && (
						<div className="flex items-center justify-between mt-4">
							<div className="text-sm text-muted-foreground">
								Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
								{Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
								{totalCount} merchants
							</div>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
									disabled={currentPage === 1 || isLoading}
								>
									Previous
								</Button>
								<div className="flex items-center gap-1">
									<span className="text-sm">
										Page {currentPage} of {totalPages}
									</span>
								</div>
								<Button
									variant="outline"
									size="sm"
									onClick={() =>
										setCurrentPage((p) => Math.min(totalPages, p + 1))
									}
									disabled={currentPage === totalPages || isLoading}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default ManageMerchantsPage;
